import {
  Component,
  computed,
  input,
  output,
} from '@angular/core'

import { FilterOperation, FilterParams } from '../shared/utilities/http-params'
import { IdrColumnDataType, IdrColumnMetadata, IdrColumnSpec, referenceDataSpecs } from './idr-columns'
import { IdrFilterColumnSelectorComponent } from './idr-filter-column-selector.component'
import { IdrFilterValueComponent } from './idr-filter-value.component'
import { IdrFilterActions } from './idr-filter-actions.component'

type OperatorSelection = Readonly<{
  label: string
  operators: readonly FilterOperation[]
  multi: boolean
  dataTypes: readonly IdrColumnDataType[]
}>

const operatorSelections: readonly OperatorSelection[] = [
  {
    label: 'equal to',
    operators: ['=='],
    multi: false,
    dataTypes: ['string', 'boolean', 'decimal', 'date', 'timestamp'],
  },
  {
    label: 'not eq. to',
    operators: ['!='],
    multi: false,
    dataTypes: ['string', 'date', 'decimal'],
  },
  {
    label: 'in list',
    operators: ['in='],
    multi: true,
    dataTypes: ['string'],
  },
  ...(['>', '<', '>=', '<='] as FilterOperation[]).map((operation): OperatorSelection => ({
    label: operation,
    operators: [operation],
    multi: false,
    dataTypes: ['string'],
  })),
  {
    label: 'between',
    operators: ['between=', '>=', '<='],
    multi: false,
    dataTypes: ['decimal', 'date', 'timestamp'],
  },
]

const isCsvSupported = (op: FilterOperation) =>
  op === 'in=' || op === 'between='

type FilterParamsField = 'name' | 'operation' | 'value'

const getDefaultOperation = (columnSpec: IdrColumnSpec): FilterOperation => {
  if (referenceDataSpecs[columnSpec.field]) {
    return 'in='
  } else if (['decimal', 'date', 'timestamp'].includes(columnSpec.dataType)) {
    return 'between='
  } else {
    return '=='
  }
}

export type RowChangeEvent = Readonly<{
  oldRow: FilterParams,
  newRow: FilterParams,
}>

@Component({
  selector: 'tr[app-idr-filter-row]',
  imports: [IdrFilterColumnSelectorComponent, IdrFilterValueComponent, IdrFilterActions],
  template: `
    <td class="name">
      <app-idr-filter-column-selector
          [row]="row()"
          [selectedFields]="selectedFields()"
          [columnSpecs]="columnMetadata().columnSpecs"
          (update)="onUpdate('name', $event)"
      />
    </td>
    <td class="operator">
      @if (columnSpec()) {
        <select
            class='form-select'
            [disabled]="operatorSelections().length < 2"
            (change)="onUpdate('operation', getEventValue($event.target!))"
        >
          @for (selection of operatorSelections(); track selection.label) {
            <option [value]="selection.operators[0]" [selected]="selection.operators.includes(operation())">
              {{ selection.label }}
            </option>
          }
        </select>
      }
    </td>
    <td class="value">
      @if (columnSpec()) {
        <app-idr-filter-value
            [row]="row()"
            [grabFocus]="grabFocus()"
            [columnSpec]="columnSpec()!"
            [values]="this.values()"
            (rowUpdate)="onRowUpdate($event)"
        />
      }
    </td>
    <td class="actions">
      <app-idr-filter-actions [row]="row()" (remove)="remove.emit($event)"/>
    </td>
  `,
  styles: `
    :host {
      td {
        vertical-align: top;
        border: none;
        padding: 4px;
        .form-control {
          font-size: 16px;
        }
      }
      td.operator {
        width: 135px;
      }
      td.actions {
        padding: 4px 0 0 0;
        width: 40px;
      }
    }
  `,
})
export class IdrFilterRowComponent {
  readonly row = input.required<FilterParams>()
  readonly selectedFields = input.required<readonly string[]>()
  readonly columnMetadata = input.required<IdrColumnMetadata>()
  readonly grabFocus = input.required<boolean>()
  readonly values = input<readonly string[]>()

  readonly update = output<RowChangeEvent>()
  readonly remove = output<FilterParams>()

  protected readonly columnSpec = computed((): IdrColumnSpec | undefined =>
    this.columnMetadata().columnSpecsByField[this.row().name],
  )

  protected readonly operation = computed((): FilterOperation =>
    this.row().values[0].operation ?? '==',
  )

  protected readonly operatorSelections = computed((): readonly OperatorSelection[] => {
    const multi = Boolean(this.values())
    if (multi) {
      return operatorSelections.filter(_ => _.multi)
    } else {
      const columnSpec = this.columnSpec()
      if (columnSpec) {
        return operatorSelections.filter(_ =>
          !_.multi && _.dataTypes.includes(columnSpec.dataType),
        )
      } else {
        return []
      }
    }
  })

  protected readonly value = computed((): string =>
    this.row().values[0].value,
  )

  protected getEventValue (target: EventTarget): string {
    return (target as HTMLSelectElement | HTMLInputElement).value
  }

  protected onUpdate(field: FilterParamsField, value: string) {
    this.onRowUpdate(this.getUpdatedRow(field, value))
  }

  protected onRowUpdate(newRow: FilterParams) {
    this.update.emit({ oldRow: this.row(), newRow })
  }

  private updateField(newField: string): FilterParams {
    return {
      name: newField,
      values: [
        {
          operation: getDefaultOperation(this.columnMetadata().columnSpecsByField[newField]),
          value: '',
        },
      ],
    }
  }

  private updateOperation(newOperation: FilterOperation): FilterParams {
    const oldOperation = this.operation()
    const oldValue = this.value()

    // when switching from 'between' to '==', for example, grab the first value of the CSV if applicable
    // so 'between 1,10'  becomes '== 1'
    const getNewValue = () => {
      if (isCsvSupported(oldOperation) && !isCsvSupported(newOperation) && oldValue.indexOf(',') > 0) {
        return oldValue.slice(0, oldValue.indexOf(','))
      } else {
        return oldValue
      }
    }

    return {
      name: this.row().name,
      values: [{ operation: newOperation, value: getNewValue() }],
    }
  }

  private updateValue(newValue: string): FilterParams {
    return {
      name: this.row().name,
      values: [{ operation: this.operation(), value: newValue }],
    }
  }

  private getUpdatedRow(field: FilterParamsField, value: string): FilterParams {
    switch (field) {
      case 'name':
        return this.updateField(value)
      case 'operation':
        return this.updateOperation(value as FilterOperation)
      default:
        return this.updateValue(value)
    }
  }
}
